// src/components/PublicClinicLanding.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { db } from '../../firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './PublicClinicLanding.css';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

const ITEMS_PER_PAGE = 6;
const COLORS = ['#4CAF50', '#FFC107', '#F44336'];

const PublicClinicLanding = () => {
  const [clinics, setClinics] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const navigate = useNavigate();
  

  useEffect(() => {
    const fetchClinics = async () => {
      const snapshot = await getDocs(query(collection(db, 'clinics'), where('public_listing', '==', true)));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const enriched = await Promise.all(
        list.map(async clinic => {
          const reviewSnap = await getDocs(query(collection(db, 'clinic_reviews'), where('clinic_id', '==', clinic.id)));
          const reviews = reviewSnap.docs.map(doc => doc.data());
          const total = reviews.length;
          const pos = reviews.filter(r => r.feeling === 'positive').length;
          const score = total > 0 ? Math.round((pos / total) * 100) : 0;
          return { ...clinic, reviews, score };
        })
      );

      const sorted = enriched.sort((a, b) => b.score - a.score);
      setClinics(sorted);
    };
    fetchClinics();
  }, []);

  const filtered = clinics.filter(c => c.name.toLowerCase().includes(searchTerm));
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="public-landing">
      <Helmet>
        <title>Top Rated Clinics | LoyalTap</title>
        <meta name="description" content="Discover top-rated clinics based on real customer reviews." />
        <link rel="canonical" href="https://loyaltap.flexpayz.com/public-clinic-landing" />
      </Helmet>
      
      <div className="language-dropdown-wrapper">
        <select onChange={(e) => i18n.changeLanguage(e.target.value)} value={i18n.language} className="language-select">
          <option value="en">🇬🇧 English</option>
          <option value="sv">🇸🇪 Svenska</option>
          <option value="ro">🇷🇴 Română</option>
        </select>
      </div>

      <h1 className='location-list'>{t('public_listing.top_rated_clinics')}</h1>

      <input
  placeholder={t('public_listing.search_clinic')}
  className="search-bar"
  type="text"
  value={searchTerm}
  onChange={(e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  }}
/>
<button className="clinic-btn back-dashboard-btn" onClick={() => navigate(-1)}>
  ⬅️ {t('manageClinics.backToDashboard')}
</button>

      <div className="location-list">
        {paginated.map(clinic => {
          const data = [
            { name: t('positive'), value: clinic.reviews.filter(r => r.feeling === 'positive').length },
            { name: t('neutral'), value: clinic.reviews.filter(r => r.feeling === 'neutral').length },
            { name: t('negative'), value: clinic.reviews.filter(r => r.feeling === 'negative').length }
          ];

          return (
            <div key={clinic.id} className="location-card">
              <h2>{clinic.name}</h2>
              <p>📍 {clinic.city || clinic.address || ''}</p>
              <p>⭐ {t('public_listing.sentiment_score')}: <strong>{clinic.score}%</strong></p>

              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data} dataKey="value" nameKey="name" outerRadius={70}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>

              <Link to={`/public-clinic-reviews/${clinic.id}`} className="view-reviews-btn">
                {t('public_listing.view_reviews')}
              </Link>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              className={currentPage === index + 1 ? 'active' : ''}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicClinicLanding;